import {
  Input as AntInput, type InputProps,
  DatePicker as AntDatePicker, type DatePickerProps,
  Radio as AntRadio, type RadioGroupProps, Button, Checkbox, type CheckboxProps,
  Spin
} from 'antd';
import locale from 'antd/es/date-picker/locale/es_ES';
import './Input.scss';
import { useRef, type SyntheticEvent } from 'react';
import { useUploadToS3 } from '../../hooks/useUploadToS3';
import clsx from 'clsx';
import { IconUpload, IconFile } from '@tabler/icons-react';

const Input = ({ placeholder, ...props }: InputProps) => {
  return (
    <div className={clsx('flex flex-col space-y-2 w-full')}>
      {placeholder && (
        <label className={clsx(
          'text-sm font-medium transition-colors duration-300',
          'text-gray-700 dark:text-gray-300'
        )}>
          {placeholder}
        </label>
      )}
      <AntInput
        {...props}
        className={clsx(
          'w-full transition-all duration-300',
          'border-gray-300 dark:border-gray-600',
          'text-gray-900 dark:text-white',
          'hover:border-blue-400 dark:hover:border-blue-500',
          'focus:border-blue-500 dark:focus:border-blue-400',
          'focus:shadow-md focus:shadow-blue-500/20 dark:focus:shadow-blue-500/10',
          'rounded-lg'
        )}
      />
    </div>
  );
};

const DatePicker = ({ placeholder, ...props }: DatePickerProps) => {
  return (
    <div className={clsx('flex flex-col space-y-2 w-full')}>
      {placeholder && (
        <label className={clsx(
          'text-sm font-medium transition-colors duration-300',
          'text-gray-700 dark:text-gray-300'
        )}>
          {placeholder}
        </label>
      )}
      <AntDatePicker
        {...props}
        locale={locale}
        className={clsx(
          'w-full transition-all duration-300',
          'border-gray-300 dark:border-gray-600',
          'text-gray-900 dark:text-white',
          'hover:border-blue-400 dark:hover:border-blue-500',
          'focus:border-blue-500 dark:focus:border-blue-400',
          'focus:shadow-md focus:shadow-blue-500/20 dark:focus:shadow-blue-500/10',
          'rounded-lg'
        )}
      />
    </div>
  );
};

const Radio = ({ name, ...props }: RadioGroupProps) => {
  return (
    <div className={clsx('flex flex-col space-y-2 w-full')}>
      {name && (
        <label className={clsx(
          'text-sm font-medium transition-colors duration-300',
          'text-gray-700 dark:text-gray-300'
        )}>
          {name}
        </label>
      )}
      <AntRadio.Group
        {...props}
        className={clsx(
          'transition-all duration-300',
          '[&_.ant-radio-wrapper]:text-gray-700',
          '[&_.ant-radio-wrapper]:dark:text-gray-300',
          '[&_.ant-radio-inner]:border-gray-400',
          '[&_.ant-radio-inner]:dark:border-gray-500',
          '[&_.ant-radio-checked_.ant-radio-inner]:!border-blue-600',
          '[&_.ant-radio-checked_.ant-radio-inner]:dark:!border-blue-500',
          '[&_.ant-radio-checked_.ant-radio-inner]:!bg-blue-600',
          '[&_.ant-radio-checked_.ant-radio-inner]:dark:!bg-blue-500',
          '[&_.ant-radio-wrapper:hover_.ant-radio-inner]:border-blue-500',
          '[&_.ant-radio-wrapper:hover_.ant-radio-inner]:dark:border-blue-400'
        )}
      />
    </div>
  );
};

const Check = ({ name, ...props }: CheckboxProps) => {
  return (
    <div className={clsx('flex items-center space-x-3 w-full')}>
      <Checkbox
        {...props}
        className={clsx(
          'transition-all duration-300',
          '[&_.ant-checkbox-inner]:border-gray-400',
          '[&_.ant-checkbox-inner]:dark:border-gray-500',
          '[&_.ant-checkbox-checked_.ant-checkbox-inner]:!border-blue-600',
          '[&_.ant-checkbox-checked_.ant-checkbox-inner]:dark:!border-blue-500',
          '[&_.ant-checkbox-checked_.ant-checkbox-inner]:!bg-blue-600',
          '[&_.ant-checkbox-checked_.ant-checkbox-inner]:dark:!bg-blue-500',
          '[&_.ant-checkbox-wrapper:hover_.ant-checkbox-inner]:border-blue-500',
          '[&_.ant-checkbox-wrapper:hover_.ant-checkbox-inner]:dark:border-blue-400'
        )}
      />
      {name && (
        <label
          onClick={(e) => {
            e.preventDefault();
            props.onChange?.({ target: { checked: !props.checked } } as any);
          }}
          className={clsx(
            'text-sm font-medium cursor-pointer transition-colors duration-300',
            'text-gray-700 dark:text-gray-300',
            'hover:text-gray-900 dark:hover:text-gray-100'
          )}
        >
          {name}
        </label>
      )}
    </div>
  );
};

interface InputFileProps {
  reset?: boolean
  name: string
  fallbackUrl: (url: string, type: string) => void
  type: 'image' | 'pdf' | 'any' | 'doc' | 'ppt'
  value?: string
}

const ACCEPTS = {
  pdf: '.pdf',
  image: 'image/*',
  doc: '.doc',
  ppt: '.ppt',
  any: 'image/*,.pdf,.ppt,.pptx,.doc,.docx'
};

const InputFile = ({ value, name, fallbackUrl, type }: InputFileProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { uploadFile, error, uploading } = useUploadToS3();

  const onClick = (event: SyntheticEvent) => {
    event.stopPropagation();
    event.preventDefault();
    fileInputRef.current?.click();
  };

  const onFileUpload = async (event: SyntheticEvent) => {
    const files = (event.target as HTMLInputElement).files;
    if (files) {
      const file = files[0];
      const url = await uploadFile(file);
      fallbackUrl(url!, files[0].type);
    }
  };

  const accepts = ACCEPTS[type];

  return (
    <div className={clsx('flex flex-col space-y-2 w-full')}>
      {name && (
        <label className={clsx(
          'text-sm font-medium transition-colors duration-300',
          'text-gray-700 dark:text-gray-300'
        )}>
          {name}
        </label>
      )}
      <div className={clsx(
        'flex items-center gap-3 p-4 rounded-lg border-2 transition-all duration-300',
        'bg-gray-50 border-gray-200',
        'dark:bg-surface-medium dark:border-borders',
        'hover:border-blue-400 dark:hover:border-blue-500',
        'hover:shadow-sm'
      )}>
        <Button
          onClick={onClick}
          disabled={uploading}
          className={clsx(
            'flex items-center gap-2 transition-all duration-300',
            'bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600',
            'border-none text-white shadow-sm hover:shadow-md',
            'disabled:opacity-50 disabled:cursor-not-allowed'
          )}
          icon={<IconUpload size={16} />}
        >
          {uploading ? 'Subiendo...' : 'Subir archivo'}
        </Button>
        {uploading && (
          <Spin size="small" />
        )}
        {value && (
          <div className={clsx(
            'flex items-center gap-2 flex-1 min-w-0',
            'text-sm text-gray-600 dark:text-gray-400'
          )}>
            <IconFile size={16} className="flex-shrink-0" />
            <span className="truncate">{value}</span>
          </div>
        )}
        <input
          ref={fileInputRef}
          className="hidden"
          type="file"
          accept={accepts}
          multiple={false}
          onChange={onFileUpload}
        />
      </div>
      {error && (
        <p className={clsx(
          'text-sm transition-colors duration-300',
          'text-red-600 dark:text-red-400'
        )}>
          {error}
        </p>
      )}
    </div>
  );
};

export enum Accept {
  text = '.txt',
}
interface InputFilePropsGeneric {
  name: string
  accept: Accept
  multiple: boolean
  returnFiles: (files: FileList) => void
}
const InputFileGeneric = ({ name, returnFiles, accept, multiple }: InputFilePropsGeneric) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const onClick = (event: SyntheticEvent) => {
    event.stopPropagation();
    event.preventDefault();
    fileInputRef.current?.click();
  };

  const onFileUpload = async (event: SyntheticEvent) => {
    const files = (event.target as HTMLInputElement).files;
    if (files) {
      returnFiles(files);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  return (
    <div className={clsx('flex flex-col space-y-2 w-full')}>
      {name && (
        <label className={clsx(
          'text-sm font-medium transition-colors duration-300',
          'text-gray-700 dark:text-gray-300'
        )}>
          {name}
        </label>
      )}
      <div className={clsx(
        'flex items-center gap-3 p-4 rounded-lg border-2 transition-all duration-300',
        'bg-gray-50 border-gray-200',
        'dark:bg-surface-medium dark:border-borders',
        'hover:border-blue-400 dark:hover:border-blue-500',
        'hover:shadow-sm'
      )}>
        <Button
          onClick={onClick}
          className={clsx(
            'flex items-center gap-2 transition-all duration-300',
            'bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600',
            'border-none text-white shadow-sm hover:shadow-md'
          )}
          icon={<IconUpload size={16} />}
        >
          Subir archivo{multiple ? 's' : ''}
        </Button>
        <input
          ref={fileInputRef}
          className="hidden"
          type="file"
          accept={accept}
          multiple={multiple}
          onChange={onFileUpload}
        />
      </div>
    </div>
  );
};

export { Input, DatePicker, Radio, InputFile, Check, InputFileGeneric };
